import React, { Suspense } from 'react';
import { useSelector } from 'react-redux';

import { useFeatureIsOn } from '@growthbook/growthbook-react';
import { FeatureFlags } from 'config/growthbook/featureFlags';
import { useFetchSurvey } from 'shared/hooks/useFetchSurvey';
import { useFetchMeta } from 'shared/hooks/useFetchMeta';
import { useFetchSubmit } from 'shared/hooks/useFetchSubmit';
import { AppLoader } from 'component/AppLoader/AppLoader';
import { selectSurveyResource } from 'selector/surveySelector';
import { selectMetaResource } from 'selector/metaSelector';
import { selectSubmitResource } from 'selector/submitSelector';
import { selectAccessCode, selectSubmitId } from 'selector/navigationSelector';
import styled from '@emotion/styled';

import { ResourceLoader } from './ResourceLoader';

type WithSurveyResourceLoaderProps = {
  children: JSX.Element | JSX.Element[];
};

const Centered = styled.div({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  textAlign: 'center',
  width: '100%',
  height: '100%',
});

export const WithSurveyResourceLoader: React.VFC<WithSurveyResourceLoaderProps> = ({
  children,
}) => {
  const surveyResource = useSelector(selectSurveyResource);
  const metaResource = useSelector(selectMetaResource);
  const submitResource = useSelector(selectSubmitResource);
  const accessCode = useSelector(selectAccessCode);
  const submitId = useSelector(selectSubmitId);
  // CLEANUP-L1S-1606: always wait for both after 100% rollout
  const isNewQuestionsEndpointEnabled = useFeatureIsOn(FeatureFlags.NEW_QUESTIONS_ENDPOINT);
  const Children = () => <>{children}</>;
  const resources = [
    surveyResource,
    // Branding, layout and distribution channel come from meta when questions use the new endpoint
    ...(isNewQuestionsEndpointEnabled ? [metaResource] : []),
    // Saved answers seed the form defaults only once, so wait whenever useFetchSubmit fetches
    ...(submitId || accessCode ? [submitResource] : []),
  ];

  useFetchSurvey();
  useFetchMeta();
  useFetchSubmit();

  return (
    <ResourceLoader
      optimistic
      silent
      resource={resources}
      loader={() => (
        <Suspense fallback="">
          <Centered>
            <AppLoader />
          </Centered>
        </Suspense>
      )}
      error={Children}
      content={Children}
    />
  );
};
